import { useForm } from '@tanstack/react-form';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UseValueSetExpandResult } from '../../hooks/useValueSetExpand';
import { DATE_FORMATS } from '../../utils/date';
import { BP_PANEL_CODE } from '../../utils/observation';
import { DateTimePicker } from '../atoms/DateTimePicker';
import { Button } from '../ui/button';
import { Field, FieldGroup, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Spinner } from '../ui/spinner';
import type { ValueSetCode } from './ValueSetCombobox';

const baseSchema = z.object({
  vitalType: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  value: z.number().positive().or(z.undefined()),
  systolic: z.number().positive().or(z.undefined()),
  diastolic: z.number().positive().or(z.undefined()),
  date: z.string().min(1),
});

const formSchema = baseSchema.superRefine((data, ctx) => {
  if (!data.vitalType) return;
  if (data.vitalType.code === BP_PANEL_CODE) {
    if (data.systolic === undefined)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['systolic'] });
    if (data.diastolic === undefined)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['diastolic'] });
  } else {
    if (data.value === undefined) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['value'] });
  }
});

type FormFieldValues = {
  vitalType: ValueSetCode | undefined;
  value: number | undefined;
  systolic: number | undefined;
  diastolic: number | undefined;
  date: string;
};

export type SimpleVitalFormValues = {
  vitalType: ValueSetCode;
  value: number;
  date: string;
  unit: string;
  unitDisplay: string;
};

export type BloodPressureFormValues = {
  vitalType: ValueSetCode;
  systolic: number;
  diastolic: number;
  date: string;
};

export type AddVitalFormValues = SimpleVitalFormValues | BloodPressureFormValues;

export type AddVitalFormInitialValues = {
  value?: number;
  systolic?: number;
  diastolic?: number;
  date?: string;
};

export type AddVitalFormContentProps = {
  onCreate: (values: AddVitalFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  /** ValueSet expansion driving the vital-type select. */
  vitalType: UseValueSetExpandResult;
  /** Pre-selected vital type shown on mount. */
  initialVitalType?: ValueSetCode;
  /** Pre-filled field values (e.g. from an existing observation). */
  initialValues?: AddVitalFormInitialValues;
  /** Fires when the user picks a vital type. Caller uses this to look up the unit. */
  onVitalTypeChange?: (value: ValueSetCode | undefined) => void;
  /** UCUM unit code for the selected vital type, sourced from LOINC's `EXAMPLE_UCUM_UNITS`. */
  unit?: string;
  /** Human-readable display string for the unit (e.g. "beats/min", "°F"). */
  unitDisplay?: string;
  /** Whether the unit lookup is in flight. */
  isUnitLoading?: boolean;
  /** Resolved label for the submit button; defaults to the translated "Add". */
  submitLabel?: string;
};

const nowLocalDateTime = (): string => format(new Date(), DATE_FORMATS.ISO_DATETIME_LOCAL);

const validateValues = (value: FormFieldValues, resolvedUnit: string): 'invalid' | undefined => {
  const base = formSchema.safeParse(value);
  if (!base.success) return 'invalid';
  if (value.vitalType?.code !== BP_PANEL_CODE && resolvedUnit.length === 0) return 'invalid';
  return undefined;
};

export function AddVitalFormContent({
  onCreate,
  onClose,
  isCreating,
  vitalType,
  initialVitalType,
  initialValues,
  onVitalTypeChange,
  unit,
  unitDisplay,
  isUnitLoading = false,
  submitLabel,
}: AddVitalFormContentProps): React.ReactNode {
  const { t } = useTranslation('records');

  const resolvedUnit = unit ?? '';
  const resolvedUnitDisplay = unitDisplay ?? resolvedUnit;

  const form = useForm({
    defaultValues: {
      vitalType: initialVitalType as ValueSetCode | undefined,
      value: initialValues?.value as number | undefined,
      systolic: initialValues?.systolic as number | undefined,
      diastolic: initialValues?.diastolic as number | undefined,
      date: initialValues?.date ?? nowLocalDateTime(),
    },
    validators: {
      onMount: formSchema,
      onChange: ({ value }) => validateValues(value, resolvedUnit),
      onSubmit: ({ value }) => validateValues(value, resolvedUnit),
    },
    onSubmit: async ({ value }) => {
      if (!value.vitalType || !value.date) return;
      if (value.vitalType.code === BP_PANEL_CODE) {
        if (value.systolic === undefined || value.diastolic === undefined) return;
        await onCreate({
          vitalType: value.vitalType,
          systolic: value.systolic,
          diastolic: value.diastolic,
          date: value.date,
        });
      } else {
        if (value.value === undefined) return;
        await onCreate({
          vitalType: value.vitalType,
          value: value.value,
          date: value.date,
          unit: resolvedUnit,
          unitDisplay: resolvedUnitDisplay,
        });
      }
      form.reset();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit().catch(() => undefined);
      }}
      className="space-y-3"
    >
      <FieldGroup>
        <form.Field name="vitalType">
          {(field) => (
            <>
              <Field>
                <FieldLabel htmlFor={field.name}>{t('vitalType')}</FieldLabel>
                <Select
                  value={field.state.value?.code ?? ''}
                  onValueChange={(v) => {
                    const item = vitalType.items.find((vt) => vt.code === v);
                    if (!item) {
                      field.handleChange(undefined);
                      onVitalTypeChange?.(undefined);
                      return;
                    }
                    field.handleChange(item);
                    onVitalTypeChange?.(item);
                  }}
                  disabled={vitalType.isFetching}
                >
                  <SelectTrigger id={field.name} aria-label={t('vitalType')} data-testid="vital-type-select">
                    {vitalType.isFetching ? (
                      <Spinner className="size-4" />
                    ) : (
                      <SelectValue placeholder={t('vitalTypePlaceholder')} />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {vitalType.items.map((vt) => (
                      <SelectItem key={vt.code} value={vt.code}>
                        {vt.display ?? vt.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {vitalType.error && <p className="text-sm text-destructive">{vitalType.error}</p>}
            </>
          )}
        </form.Field>

        <form.Subscribe selector={(state) => state.values.vitalType}>
          {(selectedVitalType) => {
            if (selectedVitalType?.code === BP_PANEL_CODE) {
              return (
                <div className="flex gap-3">
                  <form.Field name="systolic">
                    {(field) => (
                      <Field className="flex-1">
                        <FieldLabel htmlFor={field.name}>{t('systolicBP')}</FieldLabel>
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          step="any"
                          min="1"
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          onChange={(e) =>
                            field.handleChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
                          }
                          aria-label={t('systolicBP')}
                          data-testid="systolic-input"
                        />
                      </Field>
                    )}
                  </form.Field>
                  <form.Field name="diastolic">
                    {(field) => (
                      <Field className="flex-1">
                        <FieldLabel htmlFor={field.name}>{t('diastolicBP')}</FieldLabel>
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          step="any"
                          min="1"
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          onChange={(e) =>
                            field.handleChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
                          }
                          aria-label={t('diastolicBP')}
                          data-testid="diastolic-input"
                        />
                      </Field>
                    )}
                  </form.Field>
                  <Field className="flex-1">
                    <FieldLabel htmlFor="bp-unit">{t('unit')}</FieldLabel>
                    <Input id="bp-unit" name="unit" readOnly value="mmHg" aria-label={t('unit')} />
                  </Field>
                </div>
              );
            }
            return selectedVitalType ? (
              <div className="flex gap-3">
                <form.Field name="value">
                  {(field) => (
                    <Field className="flex-2">
                      <FieldLabel htmlFor={field.name}>{t('value')}</FieldLabel>
                      <Input
                        id={field.name}
                        name={field.name}
                        type="number"
                        step="any"
                        min="0"
                        value={field.state.value ?? ''}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value === '' ? undefined : e.target.valueAsNumber)}
                        aria-label={t('value')}
                        data-testid="vital-value-input"
                      />
                    </Field>
                  )}
                </form.Field>
                <Field className="flex-1">
                  <FieldLabel htmlFor="vital-unit">{t('unit')}</FieldLabel>
                  {isUnitLoading ? (
                    <div className="flex h-9 items-center justify-center rounded-md border border-input bg-muted/50">
                      <Spinner className="size-4" />
                    </div>
                  ) : (
                    <Input id="vital-unit" name="unit" readOnly value={resolvedUnitDisplay} aria-label={t('unit')} />
                  )}
                </Field>
              </div>
            ) : null;
          }}
        </form.Subscribe>

        <form.Field name="date">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('dateAndTime')}</FieldLabel>
              <DateTimePicker
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(value) => field.handleChange(value)}
                aria-label={t('dateAndTime')}
                data-testid="vital-date-input"
              />
            </Field>
          )}
        </form.Field>
      </FieldGroup>

      <div className="flex gap-2">
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || isSubmitting || isCreating || isUnitLoading}
              className="flex-1"
              aria-label={t('saveNewVital')}
              data-testid="vital-submit-button"
            >
              {isSubmitting || isCreating ? <Spinner className="size-4" /> : (submitLabel ?? t('add'))}
            </Button>
          )}
        </form.Subscribe>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelAddVital')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
